import type { ProethicaTestKind } from "@constants/demo/proethica-tests";
import type { ProethicaModuleSummary } from "@constants/demo/session-collection/proethica";
import { getCorrectAnswerIds } from "@helpers/session";
import type { Hint, Mode } from "@memorang/types";
import type {
	BaseItemSchema,
	SessionItem,
	SessionResponse,
} from "@memorang/types/src/session/new-session";
import { create } from "zustand";
import type {
	Answer,
	BlockState,
	ItemState,
	SessionState,
	UpdateItemStatesPayload,
} from "./types";

const useSessionStore = create<SessionState>()((set) => ({
	sessionItems: [],
	currentItemIndex: 0,
	currentProgress: 0,
	id: 0,
	numericId: 0,
	isSummativeTest: false,
	isDiagnostic: false,
	currentBlockIndex: 0,
	numItemsRequested: 0,
	breakTimeElapsed: 0,
	mode: "QUESTION",
	totalTime: 0,
	sessionTime: 0,
	numGoal: 0,
	inReviewMode: false,
	timeElapsed: 0,
	startTime: "",
	blockStates: new Map<number, BlockState>(),
	itemStates: new Map<number, ItemState>(),
	reviewedPrincipleKeys: new Set<string>(),
	markPrincipleReviewed: (title: string) => {
		set((state) => {
			if (state.reviewedPrincipleKeys?.has(title)) {
				return {};
			}
			return {
				reviewedPrincipleKeys: new Set(state.reviewedPrincipleKeys).add(title),
			};
		});
	},
	updateBreakTimeElapsed: (breakTimeElapsed: number) => {
		set({ breakTimeElapsed });
	},
	updateCompletedTasks: (itemId: number, completed: boolean) => {
		set((state) => {
			const completedTasks = state.completedTasks || {};
			completedTasks[itemId] = completed;
			return { completedTasks: { ...completedTasks } };
		});
	},
	updateTimeElapsed: (timeElapsed: number) => {
		set({ timeElapsed });
	},
	updateAnswerEventInProgress: (isAnswerEventInProgress: boolean) => {
		set({ isAnswerEventInProgress });
	},
	updateEndingSessionInProgress: (isEndingSessionInProgress: boolean) => {
		set({ isEndingSessionInProgress });
	},
	updateCurrentBlockIndex: (index: number) => {
		set({ currentBlockIndex: index });
	},

	toggleShowMoreItemsAlert: () => {
		set((state) => {
			return {
				showMoreItemsAlert: !state.showMoreItemsAlert,
			};
		});
	},
	toggleShowBreakDialog: (show?: boolean) => {
		set(() => {
			return {
				showBreakDialog: show,
			};
		});
	},
	toggleBreakOngoing: (isBreakOngoing: boolean) => {
		set(() => {
			return {
				isBreakOngoing,
			};
		});
	},
	updateBreak: (breakData?: {
		startTime: string;
		allotedTime: number;
		status: "STARTED" | "EXPIRED";
		totalBreakTimeTakenInSeconds?: number;
	}) => {
		if (breakData) {
			set((state) => {
				return {
					breaks: [...(state.breaks || []), breakData],
				};
			});
		} else {
			set(() => {
				return {
					breaks: [],
				};
			});
		}
	},
	updateSession: ({
		sessionResponse,
		isReverseMode,
		mode,
		isSummativeTest,
		inReviewMode,
		numItemsRequested,
		isRecommendedTask,
		isDiagnostic,
		proethicaTestKind,
		moduleId,
		moduleSummary,
		itemStates,
		answers,
		reviewedPrincipleKeys,
	}: {
		sessionResponse: SessionResponse;
		isReverseMode?: boolean;
		mode?: Mode;
		isSummativeTest?: boolean;
		inReviewMode?: boolean;
		numItemsRequested?: number;
		isRecommendedTask?: boolean;
		isDiagnostic?: boolean;
		proethicaTestKind?: ProethicaTestKind;
		moduleId?: string;
		moduleSummary?: ProethicaModuleSummary;
		itemStates?: Map<number, ItemState>;
		answers?: Map<number, Answer>;
		reviewedPrincipleKeys?: Set<string>;
	}) => {
		const initBlockStates = isSummativeTest
			? sessionResponse.sessionItems.reduce((acc, item) => {
					acc.set(item.id!, {
						currentProgress: 0,
						completed: false,
						completedStatuses: {},
					});
					return acc;
				}, new Map<number, BlockState>())
			: new Map<number, BlockState>();

		const numGoal = sessionResponse.sessionItems.length;

		// const showMoreItemsAlert =
		// 	(numItemsRequested || 0) < numGoal && !isMockedData;

		set({
			sessionItems: sessionResponse.sessionItems,
			numGoal,
			id: sessionResponse.id,
			collectionId: sessionResponse.collectionId,
			isReverseMode,
			mode,
			numericId: sessionResponse.numericId,
			isSummativeTest,
			blockStates: initBlockStates,
			inReviewMode: inReviewMode,
			// showMoreItemsAlert,
			numItemsRequested: numItemsRequested || 0,
			isRecommendedTask,
			// Latched true from creation: the report query must stay gated until
			// this session's /end round-trips (endCurrentSession's finally releases).
			isEndingSessionInProgress: true,
			isDiagnostic,
			proethicaTestKind,
			moduleId,
			moduleSummary,
			currentItemIndex: 0,
			currentProgress: 0,
			currentBlockIndex: 0,
			timeElapsed: 0,
			totalTime: 0,
			sessionTime: 0,
			isAnswerEventInProgress: false,
			breaks: [],
			breakTimeElapsed: 0,
			isBreakOngoing: false,
			completedTasks: {},
			answers,
			itemStates,
			reviewedPrincipleKeys: reviewedPrincipleKeys ?? new Set<string>(),
		});
	},
	updateCurrentItemIndex: (index: number) => set({ currentItemIndex: index }),
	setModuleId: (moduleId?: string) => set({ moduleId }),
	updateAnswers: (id: number, answer: Answer) => {
		set((state) => {
			const answers = state.answers || new Map<number, Answer>();
			const selectedChoiceIds =
				state.itemStates?.get(id)?.selectedChoiceIds || [];
			answers.set(id, {
				...answer,
				selectedChoiceIds: selectedChoiceIds,
			});
			return {
				answers: new Map(answers),
			};
		});
	},

	updateProgress: (progress: number) => {
		set((state) => {
			const { currentBlockIndex, isSummativeTest } = state;
			const blockId = state.sessionItems?.[currentBlockIndex].id;
			const existingBlockState = state.blockStates.get(blockId!);
			if (isSummativeTest) {
				const newBlockStates = new Map(state.blockStates);
				newBlockStates.set(blockId!, {
					...(existingBlockState || {}),
					currentProgress: progress,
				});
				return { blockStates: newBlockStates };
			}
			return { currentProgress: progress };
		});
	},
	updateProgressForEachBlock: (progress: number, blockId: number) => {
		set((state) => {
			const blockStates = state.blockStates || new Map<number, BlockState>();
			const blockState = blockStates.get(blockId) || {};
			blockStates.set(blockId, { ...blockState, currentProgress: progress });
			return { blockStates: new Map(blockStates) };
		});
	},
	updateItemStates: ({
		itemId,
		selectedChoiceId,
		multiSelect,
		correctAnswerIds,
		crossOut,
		scores,
		answered,
		singleReplace,
		currentOrders,
	}: UpdateItemStatesPayload) => {
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		set((state) => {
			const itemStates = state.itemStates || new Map<number, ItemState>();
			const itemState = itemStates.get(itemId);
			let selectedChoiceIds = itemState?.selectedChoiceIds || [];
			const crossedOutChoiceIds = itemState?.crossedOutChoiceIds || [];
			const hints = itemState?.hints;
			const nextCurrentOrders =
				currentOrders !== undefined ? currentOrders : itemState?.currentOrders;
			const index = selectedChoiceIds.indexOf(selectedChoiceId);
			const crossOutIndex = crossedOutChoiceIds.indexOf(selectedChoiceId);

			if (crossOut) {
				if (crossOutIndex > -1) {
					crossedOutChoiceIds.splice(crossOutIndex, 1);
				} else {
					crossedOutChoiceIds.push(selectedChoiceId);
					if (index > -1) {
						selectedChoiceIds.splice(index, 1);
					}
				}
			} else {
				if (singleReplace) {
					selectedChoiceIds = [selectedChoiceId];
				} else if (multiSelect) {
					if (index > -1) {
						selectedChoiceIds.splice(index, 1);
					} else {
						selectedChoiceIds.push(selectedChoiceId);
					}
				} else if (!(correctAnswerIds?.length || answered)) {
					if (index > -1) {
						selectedChoiceIds = [];
					} else {
						selectedChoiceIds = [selectedChoiceId];
					}
				}
				if (crossOutIndex > -1) {
					crossedOutChoiceIds.splice(crossOutIndex, 1);
				}
			}

			itemStates.set(itemId, {
				selectedChoiceIds: [...selectedChoiceIds],
				correctAnswerIds,
				crossedOutChoiceIds: [...crossedOutChoiceIds],
				scores: scores || [],
				answered: answered,
				hints,
				currentOrders: nextCurrentOrders,
				timeTaken: itemState?.timeTaken,
			});
			return { itemStates: new Map(itemStates) };
		});
	},
	// Mirrors the per-item time the summative answer-event posts into local item
	// state (merge-only: selection and grading are untouched), so the fresh
	// report sums local values that agree with the backend echo on cold load.
	recordItemTimeTaken: (itemId: number, timeTaken: number) => {
		set((state) => {
			const itemStates = state.itemStates || new Map<number, ItemState>();
			const itemState = itemStates.get(itemId);
			itemStates.set(itemId, {
				...itemState,
				scores: itemState?.scores || [],
				timeTaken,
			});
			return { itemStates: new Map(itemStates) };
		});
	},
	updateHintsForItem: (itemId: number, hints: Hint[]) =>
		set((state) => {
			const itemStates = state.itemStates || new Map();
			const itemState = itemStates.get(itemId);
			const newItemState = {
				...itemState,
				scores: itemState?.scores || [],
				hints,
			};
			itemStates.set(itemId, newItemState);
			return {
				itemStates: new Map(itemStates),
			};
		}),
	setPreviewChoiceId: (itemId: number, choiceId: string) =>
		set((state) => {
			const itemStates = state.itemStates || new Map();
			const itemState = itemStates.get(itemId);
			itemStates.set(itemId, {
				...itemState,
				scores: itemState?.scores || [],
				previewChoiceId: choiceId,
			});
			return {
				itemStates: new Map(itemStates),
			};
		}),
	// Summative tests never grade per-item, so itemStates lack `answered`/
	// `correctAnswerIds` - without them review mode renders selections as plain
	// (ungraded) and hides the correct-answer reveal. Before entering review we
	// derive the correct ids from each item's choices and flag them answered.
	gradeItemsForReview: (sessionItems: BaseItemSchema[]) =>
		set((state) => {
			const itemStates = new Map(
				state.itemStates ?? new Map<number, ItemState>(),
			);
			for (const item of sessionItems) {
				const itemId = item.id;
				if (itemId == null) {
					continue;
				}
				const existing = itemStates.get(itemId);
				itemStates.set(itemId, {
					...existing,
					// Fall back to the persisted answer echo so a review opened cold
					// (empty itemStates) still shows the learner's own selections, not
					// just the correct answers.
					selectedChoiceIds:
						existing?.selectedChoiceIds ?? item.answer?.selectedChoiceIds ?? [],
					scores: existing?.scores ?? [],
					correctAnswerIds: getCorrectAnswerIds(item),
					answered: true,
				});
			}
			return { itemStates };
		}),
	// Deliberately narrow: sets id+numericId so the hydrator's staleness gate
	// settles, but skips isEndingSessionInProgress (gates the report query) and
	// updateSession (resets block state).
	hydrateReportSession: ({
		id,
		numericId,
		sessionItems,
		moduleId,
		moduleSummary,
		proethicaTestKind,
		isSummativeTest,
		itemStates,
	}) =>
		set({
			id,
			numericId,
			sessionItems,
			moduleId,
			moduleSummary,
			proethicaTestKind,
			isSummativeTest,
			itemStates,
		}),
	updateInReview: (inReviewMode: boolean, sessionItems: SessionItem[]) =>
		set((state) => {
			if (inReviewMode) {
				return {
					inReviewMode,
					persistedCurrentProgress: state.currentProgress,
					currentProgress: 0,
					currentItemIndex: 0,
					persistedItems: state.sessionItems,
					sessionItems,
					numGoal: sessionItems.length,
				};
			}
			return {
				inReviewMode,
				currentProgress: state.persistedCurrentProgress,
				items: state.persistedSessionItems,
			};
		}),
	resetSessionStore: () =>
		set({
			// Identity must clear with the content: the report hydrator's staleness
			// gate compares the URL id against id/numericId, and a surviving id with
			// cleared itemStates/proethicaTestKind renders an empty 0-score report.
			// moduleId/sessionItems/moduleSummary deliberately SURVIVE: report screens
			// reset-then-navigate while still mounted, and their shell + "Continue to
			// next module" CTA render from these fields (b486227cc).
			id: 0,
			numericId: 0,
			currentItemIndex: 0,
			currentProgress: 0,
			itemStates: undefined,
			reviewedPrincipleKeys: new Set<string>(),
			answers: undefined,
			numGoal: 0,
			isSummativeTest: false,
			isReverseMode: false,
			inReviewMode: false,
			timeElapsed: 0,
			currentBlockIndex: 0,
			blockStates: new Map<number, BlockState>(),
			totalTime: 0,
			sessionTime: 0,
			numItemsRequested: 0,
			isEndingSessionInProgress: false,
			isAnswerEventInProgress: false,
			breaks: [],
			breakTimeElapsed: 0,
			isBreakOngoing: false,
			completedTasks: {},
			isDiagnostic: false,
			collectionId: undefined,
			proethicaTestKind: undefined,
		}),
}));

export default useSessionStore;
